import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Save, Plus, Trash2, Pencil, Truck, Percent, CreditCard, Store } from "lucide-react";
import {
  Button, Input, Select, Label, FieldHint, Modal, ModalFooter, Skeleton,
  useToast, useConfirm,
} from "@/components/ui";
import { ApiError } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import {
  storeApi, centsToStr, strToCents,
  type StoreSettings, type StoreShippingZone, type StoreShippingRate,
} from "./api";

export function StoreSettingsPage() {
  const toast = useToast();
  const [settings, setSettings] = useState<StoreSettings | null>(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    store_name: "",
    currency: "USD",
    order_prefix: "#",
    guest_checkout: true,
    receipts_enabled: true,
    manual_payments_enabled: false,
    manual_payment_instructions: "",
    tax_enabled: false,
    tax_label: "Tax",
    tax_rate: "0",           // percent, e.g. "8.5"
    tax_include_shipping: false,
  });
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    storeApi.settings()
      .then((r) => {
        setSettings(r.settings);
        const s = r.settings;
        const taxes = s.taxes ?? {};
        setForm({
          store_name: (s.store_name as string) ?? "",
          currency: (s.currency as string) ?? "USD",
          order_prefix: (s.order_prefix as string) ?? "#",
          guest_checkout: s.guest_checkout !== false,
          receipts_enabled: s.receipts_enabled !== false,
          manual_payments_enabled: s.manual_payments_enabled === true,
          manual_payment_instructions: (s.manual_payment_instructions as string) ?? "",
          tax_enabled: taxes.enabled === true,
          tax_label: taxes.label ?? "Tax",
          tax_rate: taxes.rate_bps != null ? String(taxes.rate_bps / 100) : "0",
          tax_include_shipping: taxes.include_shipping === true,
        });
      })
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Failed to load settings"));
  }, [toast]);

  const save = async () => {
    setSaving(true);
    try {
      await storeApi.saveSettings({
        store_name: form.store_name,
        currency: form.currency.toUpperCase(),
        order_prefix: form.order_prefix,
        guest_checkout: form.guest_checkout,
        receipts_enabled: form.receipts_enabled,
        manual_payments_enabled: form.manual_payments_enabled,
        manual_payment_instructions: form.manual_payment_instructions,
        taxes: {
          enabled: form.tax_enabled,
          label: form.tax_label || "Tax",
          rate_bps: Math.round((parseFloat(form.tax_rate) || 0) * 100),
          include_shipping: form.tax_include_shipping,
        },
      });
      toast.success("Saved");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (settings === null) {
    return <div className="space-y-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-16" />)}</div>;
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100">Store settings</h1>
          <p className="mt-1 text-sm text-zinc-400">Checkout, shipping, taxes and receipts.</p>
        </div>
        <Button leftIcon={<Save className="h-4 w-4" />} onClick={save} loading={saving}>Save</Button>
      </div>

      <section className="space-y-4 rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-zinc-200"><Store className="h-4 w-4 text-rose-300" /> General</h2>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="st-name">Store name</Label>
            <Input id="st-name" value={form.store_name} onChange={(e) => set("store_name", e.target.value)} />
            <FieldHint>Used on the site, receipts, and the payment page.</FieldHint>
          </div>
          <div>
            <Label htmlFor="st-cur">Currency</Label>
            <Select id="st-cur" value={form.currency} onChange={(e) => set("currency", e.target.value)}>
              {["USD", "CAD", "EUR", "GBP", "AUD", "NZD", "JPY", "CHF", "SEK", "MXN"].map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap gap-5">
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <input type="checkbox" checked={form.guest_checkout} onChange={(e) => set("guest_checkout", e.target.checked)} className="accent-rose-500" />
            Allow guest checkout (no account required)
          </label>
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <input type="checkbox" checked={form.receipts_enabled} onChange={(e) => set("receipts_enabled", e.target.checked)} className="accent-rose-500" />
            Email receipts & shipping confirmations
          </label>
        </div>
      </section>

      <section className="space-y-4 rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-zinc-200"><CreditCard className="h-4 w-4 text-rose-300" /> Payments</h2>
        <p className="text-sm text-zinc-400">
          Card payments use the provider connected in{" "}
          <Link to="/payments" className="text-rose-400 hover:underline">Payments → Settings</Link>{" "}
          (Stripe or PayPal, via SyncSuite → Connections). Products can also carry their own hosted
          payment link for other processors.
        </p>
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input type="checkbox" checked={form.manual_payments_enabled} onChange={(e) => set("manual_payments_enabled", e.target.checked)} className="accent-rose-500" />
          Accept manual payments (bank transfer, pay on pickup…)
        </label>
        {form.manual_payments_enabled && (
          <div>
            <Label htmlFor="st-manual">Instructions shown to the customer</Label>
            <Input id="st-manual" value={form.manual_payment_instructions} onChange={(e) => set("manual_payment_instructions", e.target.value)}
              placeholder="Send an e-transfer to pay@yourstore.com and we'll confirm within a day." />
            <FieldHint>Orders stay "Pending payment" until you mark them paid under Orders.</FieldHint>
          </div>
        )}
      </section>

      <section className="space-y-4 rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-zinc-200"><Percent className="h-4 w-4 text-rose-300" /> Taxes</h2>
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input type="checkbox" checked={form.tax_enabled} onChange={(e) => set("tax_enabled", e.target.checked)} className="accent-rose-500" />
          Charge tax at checkout
        </label>
        {form.tax_enabled && (
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="st-taxlabel">Label</Label>
              <Input id="st-taxlabel" value={form.tax_label} onChange={(e) => set("tax_label", e.target.value)} placeholder="Sales tax / VAT / GST" />
            </div>
            <div>
              <Label htmlFor="st-taxrate">Rate (%)</Label>
              <Input id="st-taxrate" value={form.tax_rate} onChange={(e) => set("tax_rate", e.target.value)} inputMode="decimal" />
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input type="checkbox" checked={form.tax_include_shipping} onChange={(e) => set("tax_include_shipping", e.target.checked)} className="accent-rose-500" />
                Tax shipping too
              </label>
            </div>
          </div>
        )}
      </section>

      <ShippingSection currency={form.currency} />

      <div className="flex justify-end">
        <Button leftIcon={<Save className="h-4 w-4" />} onClick={save} loading={saving}>Save</Button>
      </div>
    </div>
  );
}

// ─── Shipping zones & rates ─────────────────────────────────────────────────
function ShippingSection({ currency }: { currency: string }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [zones, setZones] = useState<StoreShippingZone[] | null>(null);
  const [editingZone, setEditingZone] = useState<StoreShippingZone | "new" | null>(null);
  const [editingRate, setEditingRate] = useState<{ zone: StoreShippingZone; rate: StoreShippingRate | null } | null>(null);

  const load = useCallback(() => {
    storeApi.shipping().then((r) => setZones(r.zones)).catch(() => setZones([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const removeZone = async (z: StoreShippingZone) => {
    if (!(await confirm({ title: `Delete zone "${z.name}"?`, description: "Its rates go with it.", confirmLabel: "Delete", danger: true }))) return;
    try { await storeApi.deleteZone(z.id); load(); } catch { toast.error("Failed"); }
  };
  const removeRate = async (r: StoreShippingRate) => {
    if (!(await confirm({ title: `Delete rate "${r.name}"?`, confirmLabel: "Delete", danger: true }))) return;
    try { await storeApi.deleteRate(r.id); load(); } catch { toast.error("Failed"); }
  };

  return (
    <section className="space-y-4 rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-zinc-200"><Truck className="h-4 w-4 text-rose-300" /> Shipping</h2>
        <Button size="sm" variant="secondary" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditingZone("new")}>Add zone</Button>
      </div>
      <FieldHint>
        Zones group the countries you ship to; each zone offers one or more flat rates. A rate with a
        "free over" amount becomes free once the cart reaches it.
      </FieldHint>

      {zones === null ? (
        <Skeleton className="h-24" />
      ) : zones.length === 0 ? (
        <p className="text-sm text-zinc-500">No shipping zones — checkout will show no shipping options for physical goods.</p>
      ) : (
        <div className="space-y-3">
          {zones.map((z) => (
            <div key={z.id} className="rounded-lg border border-zinc-800 bg-zinc-950 p-3">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <span className="font-medium text-zinc-100">{z.name}</span>
                  <span className="ml-2 text-xs text-zinc-500">
                    {z.countries.includes("*") ? "Everywhere" : z.countries.join(", ")}
                  </span>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setEditingRate({ zone: z, rate: null })} leftIcon={<Plus className="h-3.5 w-3.5" />}>Rate</Button>
                  <button onClick={() => setEditingZone(z)} className="rounded p-1.5 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => void removeZone(z)} className="rounded p-1.5 text-zinc-500 hover:bg-zinc-800 hover:text-rose-300"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              {z.rates.length > 0 && (
                <div className="mt-2 space-y-1">
                  {z.rates.map((r) => (
                    <div key={r.id} className="flex items-center gap-2 rounded-md bg-zinc-900/60 px-2.5 py-1.5 text-sm">
                      <span className="min-w-0 flex-1 truncate text-zinc-200">{r.name}</span>
                      <span className="text-zinc-300">{r.amount_cents === 0 ? "Free" : formatMoney(r.amount_cents, currency)}</span>
                      {r.free_over_cents != null && (
                        <span className="text-xs text-emerald-400">free over {formatMoney(r.free_over_cents, currency)}</span>
                      )}
                      <button onClick={() => setEditingRate({ zone: z, rate: r })} className="rounded p-1 text-zinc-500 hover:text-zinc-200"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => void removeRate(r)} className="rounded p-1 text-zinc-500 hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editingZone !== null && (
        <ZoneModal
          zone={editingZone === "new" ? null : editingZone}
          onClose={() => setEditingZone(null)}
          onSaved={() => { setEditingZone(null); load(); }}
        />
      )}
      {editingRate !== null && (
        <RateModal
          zone={editingRate.zone}
          rate={editingRate.rate}
          currency={currency}
          onClose={() => setEditingRate(null)}
          onSaved={() => { setEditingRate(null); load(); }}
        />
      )}
    </section>
  );
}

function ZoneModal({ zone, onClose, onSaved }: {
  zone: StoreShippingZone | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState(zone?.name ?? "");
  const [everywhere, setEverywhere] = useState(zone ? zone.countries.includes("*") : true);
  const [countries, setCountries] = useState(zone && !zone.countries.includes("*") ? zone.countries.join(", ") : "");

  const save = async () => {
    if (!name.trim()) { toast.error("Please name the zone."); return; }
    const list = everywhere
      ? ["*"]
      : countries.split(",").map((s) => s.trim().toUpperCase()).filter((s) => /^[A-Z]{2}$/.test(s));
    if (!everywhere && list.length === 0) {
      toast.error("Enter two-letter country codes, like US, CA, GB.");
      return;
    }
    setSaving(true);
    try {
      if (zone) await storeApi.updateZone(zone.id, { name: name.trim(), countries: list });
      else await storeApi.createZone({ name: name.trim(), countries: list });
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={zone ? "Edit zone" : "New shipping zone"} size="sm">
      <div className="space-y-4 p-5">
        <div>
          <Label htmlFor="z-name">Zone name</Label>
          <Input id="z-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="North America" />
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input type="checkbox" checked={everywhere} onChange={(e) => setEverywhere(e.target.checked)} className="accent-rose-500" />
          Ships everywhere (rest of world)
        </label>
        {!everywhere && (
          <div>
            <Label htmlFor="z-countries">Countries (2-letter codes, comma-separated)</Label>
            <Input id="z-countries" value={countries} onChange={(e) => setCountries(e.target.value)} placeholder="US, CA, MX" />
          </div>
        )}
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={save} loading={saving}>Save</Button>
      </ModalFooter>
    </Modal>
  );
}

function RateModal({ zone, rate, currency, onClose, onSaved }: {
  zone: StoreShippingZone;
  rate: StoreShippingRate | null;
  currency: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: rate?.name ?? "",
    amount: rate ? centsToStr(rate.amount_cents) : "5.00",
    free_over: rate?.free_over_cents != null ? centsToStr(rate.free_over_cents) : "",
  });

  const save = async () => {
    if (!form.name.trim()) { toast.error("Please name the rate."); return; }
    setSaving(true);
    const body = {
      name: form.name.trim(),
      amount_cents: strToCents(form.amount),
      free_over_cents: form.free_over ? strToCents(form.free_over) : null,
    };
    try {
      if (rate) await storeApi.updateRate(rate.id, body);
      else await storeApi.createRate(zone.id, body);
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={rate ? "Edit rate" : `New rate — ${zone.name}`} size="sm">
      <div className="space-y-4 p-5">
        <div>
          <Label htmlFor="r-name">Name</Label>
          <Input id="r-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Standard shipping" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="r-amt">Price ({currency})</Label>
            <Input id="r-amt" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} inputMode="decimal" />
          </div>
          <div>
            <Label htmlFor="r-free">Free over ({currency})</Label>
            <Input id="r-free" value={form.free_over} onChange={(e) => setForm((f) => ({ ...f, free_over: e.target.value }))} inputMode="decimal" placeholder="never" />
          </div>
        </div>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={save} loading={saving}>Save</Button>
      </ModalFooter>
    </Modal>
  );
}
